import {MithrilComponent} from '@bitstillery/mithril'
import m from '@bitstillery/mithril'
import {
    Button,
    CollectionHeader,
    CollectionItems,
    CollectionSorter,
    CollectionStatus,
    CollectionView,
    CollectionViewToggle,
    Icon,
    PanelFilters,
} from '@bitstillery/common/components/portal'
import {build_transforms, CollectionProxy} from '@bitstillery/common/lib/collection'
import {activated_filters} from '@bitstillery/common/lib/filters'
import {classes, PlausibleCartSource} from '@bitstillery/common/lib/utils'
import {html_element} from '@bitstillery/common/lib/dom'
import {unchecked} from '@bitstillery/common/lib/unchecked'
import type {RelationFavouriteResult} from '@bitstillery/common/api/fact2server'
import type {FilterSelectMultipleState} from '@bitstillery/common/types'
import {$t, api} from '@bitstillery/common/app'
import {format_money_with_symbol} from '@bitstillery/common/lib/format'

import {PanelCart, PanelCategories} from '@/components/panels'
import {$m, $s} from '@/app'
import {panel_cart_peek_units} from '@/components/panels/panel_cart/panel_cart_state'
import {close_portal_shop_overlays} from '@/lib/shop_portal_overlays'
import {observe_shop_grid} from '@/lib/shop_grid_balance'
import {CollectionProduct} from '@/components/collection/product/product'
import type {CollectionItemOptions} from '@/components/collection/product/product'
import {OfferItemDetailDialog} from '@/components/pages/offers/lib/offer_item_detail_dialog'
import type {CartOfferItem} from '@/models/cart'

/** The filters `portal/favourites/collection-view/meta` describes. */
type FavouriteFilters = {availability: FilterSelectMultipleState}

export const collection = new CollectionProxy<CartOfferItem, FavouriteFilters>()

export class FavouriteList extends MithrilComponent {
    private stop_grid_balance?: () => void

    override oncreate(vnode: m.VnodeDOM) {
        const dom = html_element(vnode.dom)
        if (dom) {
            this.stop_grid_balance = observe_shop_grid(dom)
        }
    }

    override onremove() {
        this.stop_grid_balance?.()
    }

    override async oninit() {
        $s.panels.view.menu.collapsed = true
        $s.panels.view.categories.collapsed = true

        const transforms = build_transforms<RelationFavouriteResult[], CartOfferItem, unknown, FavouriteFilters>({
            with_items_queried: async ({result, status_code}) => {
                if (status_code === 404) {
                    return []
                }
                if (status_code > 299) {
                    return []
                }

                const response = result
                const case_artkeys = response.map((i) => i.case_artkey)
                await api.post('portal/offer-items/favourites/mark-update-seen', {case_artkeys: case_artkeys}, true)

                const offer_item_artkeys = response.flatMap((i) => (i.artkey === null ? [] : [i.artkey]))

                await $m.offer.load_product_photos(offer_item_artkeys)

                const stat_resp = await api.get('portal/offer-items/favourites/statistics')
                // The badge count is secondary to the list; on failure it stays stale.
                if (stat_resp.success) $s.offers.updated_favourites = stat_resp.result.updated_favourites
                // The product cards file each row as a cart offer item. A favourite whose product has no offer
                // now arrives with a null artkey and null prices, which that type doesn't allow for.
                return unchecked<CartOfferItem[]>(result)
            },
        })

        await collection.init(
            {
                endpoint: {
                    meta: true,
                    method: 'get',
                    path: 'portal/favourites/collection-view',
                },
                page_size: 30,
            },
            undefined,
            transforms,
        )
        {
            // Read back from localStorage, which may hold a mode this build no longer has.
            const m: string = $s.shop.collection_view_mode
            if (m === 'list' || m === 'grid') {
                collection.state.view.mode = m
            }
        }
    }

    private item_renderer = (row: CartOfferItem | null, opts: CollectionItemOptions) => {
        return (
            <CollectionProduct
                collection={collection}
                global_index={opts.global_index}
                offer_item={row}
                on_item_height_change={opts.on_item_height_change}
                source={PlausibleCartSource.OFFERS}
                variant='offer'
                virtualize={true}
            />
        )
    }

    private toggle_categories() {
        if (!$s.panels.view.categories.collapsed) {
            $s.panels.view.categories.collapsed = true
        } else {
            $s.panels.view.menu.collapsed = true
            $s.panels.view.filters.collapsed = true
            $s.panels.context.collapsed = true
            $s.panels.view.categories.collapsed = false
        }
    }

    private toggle_filters() {
        if (!$s.panels.view.filters.collapsed) {
            $s.panels.view.filters.collapsed = true
        } else {
            $s.panels.view.menu.collapsed = true
            $s.panels.view.categories.collapsed = true
            $s.panels.context.collapsed = true
            $s.panels.view.filters.collapsed = false
        }
    }

    private toggle_cart() {
        if (!$s.panels.context.collapsed) {
            $s.panels.context.collapsed = true
        } else {
            $s.panels.view.menu.collapsed = true
            $s.panels.view.filters.collapsed = true
            $s.panels.view.categories.collapsed = true
            $s.panels.context.collapsed = false
        }
    }

    view(): m.Children {
        const _has_errors = $m.cart.has_errors()
        const filters_open = !$s.panels.view.filters.collapsed
        const cart_open = !$s.panels.context.collapsed
        const categories_open = !$s.panels.view.categories.collapsed
        const any_overlay_open = filters_open || cart_open || categories_open
        const filter_count = activated_filters(collection)

        return (
            <div className='c-offer shop-grid'>
                <div
                    className={classes('shop-backdrop', {
                        'shop-backdrop-visible': any_overlay_open,
                    })}
                    onclick={() => close_portal_shop_overlays()}
                />

                <div
                    className={classes('shop-panel shop-panel-filters', 'shop-overlay shop-overlay-bottom', 'shop-overlay-left', {
                        'shop-overlay-open': filters_open,
                    })}
                >
                    <PanelFilters collection={collection} no_float={true} />
                </div>

                <div
                    className={classes('shop-panel shop-panel-categories', 'shop-panel-categories--sheet-surface', {
                        'shop-overlay shop-overlay-bottom': true,
                        'shop-overlay-open': categories_open,
                    })}
                >
                    <PanelCategories
                        on_close={() => {
                            $s.panels.view.categories.collapsed = true
                        }}
                    />
                </div>

                <CollectionView mode={collection.state.view.mode === 'list' ? 'list' : 'grid'}>
                    <CollectionHeader>
                        <div className='actions'>
                            <CollectionStatus
                                collection={collection}
                                loading={$t('collection.loading_products')}
                                virtualize={true}
                            />
                            <div className='shop-collection-controls'>
                                <CollectionSorter collection={collection} />
                                <CollectionViewToggle
                                    collection={collection}
                                    on_mode_change={(mode) => {
                                        $s.shop.collection_view_mode = mode
                                    }}
                                />
                            </div>
                        </div>
                    </CollectionHeader>
                    <CollectionItems collection={collection} item={this.item_renderer} overscan_rows={2} virtualize={true} />
                </CollectionView>

                <PanelCart mode='shop' />

                <div className='shop-surface-toggles'>
                    <div className='shop-surface-toggles-tablet'>
                        <div className='shop-floating-toggles'>
                            {/* Tablet portrait only (CSS-hidden from breakpoint-30 up, where the
                                breadcrumb band fits the full category menu). */}
                            <div className='shop-categories-toggle'>
                                <Button
                                    active={categories_open}
                                    icon='categories'
                                    onclick={() => this.toggle_categories()}
                                    type='info'
                                />
                            </div>
                            <div className='shop-filters-toggle'>
                                <Button
                                    active={filters_open}
                                    icon='filter'
                                    onclick={() => this.toggle_filters()}
                                    type='info'
                                    variant='toggle'
                                >
                                    {filter_count > 0 ? (
                                        <span className='shop-cart-toggle-badge'>
                                            {filter_count > 999 ? '999+' : String(filter_count)}
                                        </span>
                                    ) : null}
                                </Button>
                            </div>
                            <div className='shop-cart-toggle'>
                                <Button
                                    active={cart_open}
                                    icon='cart'
                                    onclick={() => this.toggle_cart()}
                                    type='info'
                                    variant='toggle'
                                >
                                    {$m.cart.cart_units() > 0 ? (
                                        <span className='shop-cart-toggle-badge'>
                                            {$m.cart.cart_units() > 999 ? '999+' : String($m.cart.cart_units())}
                                        </span>
                                    ) : null}
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className='shop-surface-toggles-desktop'>
                        <div className='shop-cart-toggle'>
                            <Button
                                active={cart_open}
                                icon='cart'
                                onclick={() => this.toggle_cart()}
                                type='info'
                                variant='toggle'
                            >
                                {$m.cart.cart_units() > 0 ? (
                                    <span className='shop-cart-toggle-badge'>
                                        {$m.cart.cart_units() > 999 ? '999+' : String($m.cart.cart_units())}
                                    </span>
                                ) : null}
                            </Button>
                        </div>
                    </div>
                </div>

                <div
                    className={classes('shop-bottom-bar', {
                        'shop-bottom-bar--cart-peek': $m.cart.cart_amount_validated() > 0 || $m.cart.booked_amount() > 0,
                    })}
                >
                    <Button
                        active={categories_open}
                        className='shop-bottom-bar-categories'
                        icon='categories'
                        onclick={() => this.toggle_categories()}
                        text={$t('panel_categories.title')}
                        type={categories_open ? 'info' : 'default'}
                    />
                    <Button
                        active={filters_open}
                        className='shop-bottom-bar-filters'
                        icon='filter'
                        onclick={() => this.toggle_filters()}
                        text={filter_count > 0 ? `Filters (${filter_count})` : 'Filters'}
                        type={filters_open ? 'info' : 'default'}
                    />
                    {$m.cart.cart_amount_validated() > 0 || $m.cart.booked_amount() > 0 ? (
                        <button
                            aria-expanded={cart_open}
                            className={classes('shop-mobile-cart-peek', {active: cart_open})}
                            type='button'
                            onclick={() => this.toggle_cart()}
                        >
                            <Icon name='cart' size='s' type='unset' />
                            <span className='shop-mobile-cart-peek-count'>{panel_cart_peek_units()}</span>
                            <span className='shop-mobile-cart-peek-total'>
                                {format_money_with_symbol(
                                    $m.cart.total_amount_validated(),
                                    $s.identity.user.currency,
                                    $s.language,
                                )}
                            </span>
                        </button>
                    ) : null}
                </div>

                <OfferItemDetailDialog collection={collection} />
            </div>
        )
    }
}
